import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { createParentGridOptions } from '@app/Shared/grid/data-explorer-grid-options';
import { ToolsService } from '@app/Services/Utilities/tools.service';
import { iParsedEntity } from '@app/Shared/interfaces/iEntityParsed';
import { pluralize } from '@app/Shared/util/stringManipulation';
import { AppState, DashboardActions } from '@app/Store';
import { dashboardFeatureSelectors } from '@app/Store/features/dashboard.feature';
import { select, Store } from '@ngrx/store';
import { CheckboxSelectionCallbackParams, ColDef, GridApi, GridOptions, GridReadyEvent, RowSelectedEvent } from 'ag-grid-community';
import { combineLatest, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-parent-section',
    templateUrl: './parent-section.component.html',
    styleUrls: ['./parent-section.component.scss'],
})
export class ParentSectionComponent implements OnInit, OnDestroy {
    public parentGroups: Array<{ name: string; data: any[]; expanded: boolean; gridOptions: GridOptions; gridApi?: GridApi }> = [];
    public parentDropdownDataSource: any[] | null = null;
    public selectedParentDropdownData: any;
    public activeChange: boolean = true;
    public activeRemove: boolean = true;
    public activeAdd: boolean = true;
    public masterInfo: any = { masterTable: null, masterKey: null, linkTable: null, parentInfo: null, linkKey: null };
    public selectedEntity: iParsedEntity | null = null;
    public noRecordSelected: boolean = true;

    private subscription: Subscription = new Subscription();

    constructor(private store: Store<AppState>, private cdr: ChangeDetectorRef, private toolsService: ToolsService) {}

    ngOnInit(): void {
        // Subscribe to selected entity and selected records
        this.subscription.add(
            combineLatest(this.store.pipe(select(dashboardFeatureSelectors.selectSelectedEntity)), this.store.pipe(select(dashboardFeatureSelectors.selectSelectedRecords)))
                .pipe(
                    distinctUntilChanged((prev, curr) => {
                        const [prevEntity, prevRecords] = prev;
                        const [currEntity, currRecords] = curr;
                        const entityChanged = prevEntity?.entityName !== currEntity?.entityName;
                        if (entityChanged) return false;
                        if (prevRecords?.length !== currRecords?.length) return false;
                        if (prevRecords?.length === 0 && currRecords?.length === 0) return true;
                        const prevKey = prevRecords?.[0]?.data?.[prevEntity?.key];
                        const currKey = currRecords?.[0]?.data?.[currEntity?.key];
                        return prevKey === currKey;
                    })
                )
                .subscribe(([entity, selectedRecords]) => {
                    console.log('parent section', entity, selectedRecords);
                    if (entity && selectedRecords && selectedRecords.length > 0) {
                        this.selectedEntity = entity;
                        const selectedRow = selectedRecords[0].data;
                        const masterKey = selectedRow[entity.key];
                        this.masterInfo.masterTable = pluralize(entity.entityName);
                        this.masterInfo.masterKey = masterKey;
                        this.noRecordSelected = false;
                        this.fetchParentData(entity, masterKey);
                    } else {
                        this.noRecordSelected = true;
                        this.selectedEntity = null;
                        this.masterInfo = { masterTable: null, masterKey: null, linkTable: null, parentInfo: null, linkKey: null };
                        this.parentDropdownDataSource = null;
                        this.parentGroups = [];
                    }
                })
        );
        // Subscribe to parent links data
        this.subscription.add(
            this.store
                .pipe(select(dashboardFeatureSelectors.selectParentLinksData))
                .pipe(debounceTime(100))
                .subscribe((parentLinksData) => {
                    this.handleDataAndGrid(parentLinksData);
                })
        );

        // Subscribe to parent grid selected row
        this.subscription.add(
            this.store.pipe(select(dashboardFeatureSelectors.selectParentGridCurrentRowData)).subscribe((currentRow) => {
                if (currentRow == null) {
                    this.masterInfo.parentInfo = null;
                } else {
                    // Try to find LinkKey, SoldKey, or UserId
                    const linkKey = currentRow.LinkKey || currentRow.SoldKey || currentRow.UserId;
                    this.masterInfo.parentInfo = {
                        linkKey: linkKey,
                    };
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
    private fetchParentData(entity: iParsedEntity, masterKey: string): void {
        const masterTable = pluralize(entity.entityName);
        this.store.dispatch(DashboardActions.fetchParentData({ masterTable, masterKey }));
    }

    private handleDataAndGrid(linksData: any[]) {
        // Fill master info with link table name
        this.fillMasterInfo(linksData);

        const resultsPropName = this.toolsService.getPropNameCaseSensitive(linksData, 'results');
        if (linksData && resultsPropName && linksData[resultsPropName] != null && linksData[resultsPropName].length > 0) {
            const dropdownDataSource: any[] = [];
            const groups: Array<{ name: string; data: any[]; expanded: boolean; gridOptions: GridOptions; gridApi?: GridApi }> = [];

            for (let index = 0; index < linksData[resultsPropName].length; index++) {
                const data = linksData[resultsPropName][index];
                const dataPropName = this.toolsService.getPropNameCaseSensitive(data, 'data');

                // Use case-sensitive property lookup for RelationUIName as well
                const relationUIPropName = this.toolsService.getPropNameCaseSensitive(data, 'RelationUIName') || 'RelationUIName';
                const linkTablePropName = this.toolsService.getPropNameCaseSensitive(data, 'LinkTableName') || 'LinkTableName';

                const enrichedData = (dataPropName && data[dataPropName] ? data[dataPropName] : []).map((obj: any) => ({
                    ...obj,
                    DataSetName: data[relationUIPropName],
                    RelationUIName: data[relationUIPropName],
                    LinkTableName: data[linkTablePropName],
                }));
                const dropdownData = {
                    id: index + 1,
                    title: data[relationUIPropName] || `Group ${index + 1}`,
                    data: enrichedData,
                    LinkTableName: data[linkTablePropName],
                };
                dropdownDataSource.push(dropdownData);

                // Create a group for this data with its own grid (even if empty)
                const groupName = data[relationUIPropName] || `Group ${index + 1}`;
                const gridOptions = enrichedData.length > 0 ? this.createGroupGridOptions(enrichedData) : createParentGridOptions(this.isFirstColumn);
                groups.push({
                    name: groupName,
                    data: enrichedData,
                    expanded: false,
                    gridOptions: gridOptions,
                });
            }
            const hasData = dropdownDataSource && dropdownDataSource.length > 0 && dropdownDataSource[0].data && dropdownDataSource[0].data.length > 0;
            if (hasData) {
                // Try to find LinkKey, SoldKey, or UserId from first group
                const firstGroupData = dropdownDataSource[0].data;
                const firstRow = firstGroupData[0];
                this.masterInfo.linkKey = firstRow.LinkKey || firstRow.SoldKey || firstRow.UserId || null;
                // Set linkTable from first dropdown item if not already set
                if (!this.masterInfo.linkTable && dropdownDataSource[0].LinkTableName) {
                    this.masterInfo.linkTable = dropdownDataSource[0].LinkTableName;
                }
            }
            this.selectedParentDropdownData = dropdownDataSource[0]?.id;
            this.parentDropdownDataSource = [...dropdownDataSource];

            this.parentGroups = groups;
            this.updateButtonState(groups);
        } else {
            this.parentDropdownDataSource = [];
            this.parentGroups = [];
            this.updateButtonState([]);
            this.selectedParentDropdownData = null;
        }
        this.cdr.detectChanges();
    }
    private createGroupGridOptions(data: any[]): GridOptions {
        if (data.length === 0) {
            const gridOptions = createParentGridOptions(this.isFirstColumn);
            gridOptions.pagination = false;
            gridOptions.suppressPaginationPanel = true;
            return gridOptions;
        }

        // Build columns based on the first row's properties
        const firstRow = data[0];
        const keys = Object.keys(firstRow);

        // Filter out metadata fields
        const displayKeys = keys.filter((key) => key !== 'RelationUIName' && key !== 'LinkTableName' && key !== 'DataSetName');

        const columnDefs: ColDef[] = displayKeys.map((key, index) => ({
            field: key,
            headerName: key,
            sortable: true,
            filter: true,
            flex: 1,
            minWidth: 100,
            resizable: true,
            pinned: index < 2 ? 'left' : undefined,
        }));
        const gridOptions = createParentGridOptions(this.isFirstColumn);
        gridOptions.columnDefs = columnDefs;
        gridOptions.rowData = data;
        gridOptions.pagination = false;
        gridOptions.suppressPaginationPanel = true;
        return gridOptions;
    }

    public toggleGroup(index: number): void {
        if (this.parentGroups[index]) {
            this.parentGroups[index].expanded = !this.parentGroups[index].expanded;
            this.cdr.detectChanges();
        }
    }
    public onGroupGridReady(index: number, event: GridReadyEvent): void {
        if (this.parentGroups[index]) {
            this.parentGroups[index].gridApi = event.api;
            const group = this.parentGroups[index];

            // Set columns and data explicitly via API
            if (group.gridOptions.columnDefs && group.gridOptions.columnDefs.length > 0) {
                event.api.setColumnDefs(group.gridOptions.columnDefs);
            }

            if (group.data && group.data.length > 0) {
                event.api.setRowData(group.data);
            }

            // Auto-size all columns to fit their content
            setTimeout(() => {
                const allColumnIds: string[] = [];
                const columns = event.api.getColumns();
                if (columns) {
                    columns.forEach((column: any) => {
                        if (column) {
                            allColumnIds.push(column.getColId());
                        }
                    });
                }
                if (allColumnIds.length > 0) {
                    event.api.autoSizeColumns(allColumnIds);
                }
            }, 100);
        }
    }
    public onGroupRowSelected(index: number, event: RowSelectedEvent): void {
        const selectedRows = event.api.getSelectedRows();
        if (selectedRows.length > 0 && this.parentGroups[index]) {
            this.store.dispatch(DashboardActions.changeParentGridSelectedData({ selectedRow: selectedRows[0] }));
        }
    }

    private fillMasterInfo(parentLinksData: any[]) {
        const prData: any = parentLinksData;
        if (prData && !Array.isArray(prData) && prData.results && prData.results.length > 0) {
            this.masterInfo.linkTable = prData.results[0].LinkTableName;
        }
    }
    public onParentDataSourceClick(item: any) {
        // This method is kept for backward compatibility but may not be used in the new layout
        this.masterInfo.linkTable = item.LinkTableName;
        this.selectedParentDropdownData = item.id;
        // Note: With the new group-based layout, this method may not be needed
        // Groups are automatically created from the data in handleDataAndGrid
    }

    private updateButtonState(groups: any[]): void {
        const hasData = groups && groups.length > 0 && groups.some((g) => g.data && g.data.length > 0);
        if (hasData) {
            this.activeChange = true;
            this.activeRemove = true;
            this.activeAdd = false;
        } else {
            this.activeChange = false;
            this.activeRemove = false;
            this.activeAdd = true;
        }
    }

    private isFirstColumn = (params: CheckboxSelectionCallbackParams): boolean => {
        const displayedColumns = params?.columnApi?.getAllDisplayedColumns() || [];
        return displayedColumns[0] === params.column;
    };
}
